// @ts-nocheck
import { JxsComponent } from '@jxs/component';
import { isBlank } from '@jxs/utilities';

/**
 * 封装Plyr(https://plyr.io/)的视频播放器，需要在全局引入plyr的依赖库。
 */
class JxsVideo extends JxsComponent {

  #ready = false;
  #clearDestroyTimeoutId = null;

  constructor() {
    super();
    this.player = null;
  }

  connectedCallback() {
    super.connectedCallback();
    clearTimeout(this.#clearDestroyTimeoutId);
    // 一般player实例不可用，可能是首次初始化或者克隆组件，需要重新初始化。
    if (!this.player) {
      this.#initSettings();
      this.#prepareResponsiveVideo();
      this.#initPlayer();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    /*
      延迟销毁，避免在轮播这个组件中，在loop模式下卡片更换DOM位置
      导致视频组件频繁的销毁和初始化。
    */
    this.#clearDestroyTimeoutId = setTimeout(() => {
      this.player?.destroy();
      this.#ready = false;
      if (this._cleanAutoplay) {
        this._cleanAutoplay();
      }
    }, 500);
  }

  #initSettings() {
    if (this.getDataset('parentSetting')) {
      const settingElement = this.closest('[jxs-video-setting]');
      if (settingElement) {
        if (!isBlank(settingElement.dataset.controls)) {
          this.dataset.controls = settingElement.dataset.controls;
        }
        if (!isBlank(settingElement.dataset.loop)) {
          this.dataset.loop = settingElement.dataset.loop;
        }
        if (!isBlank(settingElement.dataset.autoplay)) {
          this.dataset.autoplay = settingElement.dataset.autoplay;
        }
        if (!isBlank(settingElement.dataset.muted)) {
          this.dataset.muted = settingElement.dataset.muted;
        }
        if (!isBlank(settingElement.dataset.pauseOnLeave)) {
          this.dataset.pauseOnLeave = settingElement.dataset.pauseOnLeave;
        }
      }
    }
  }

  /**
   * 从template里选择视频源。
   * 注：template不能删除，后续重新初始化需要用到。
   */
  #prepareResponsiveVideo() {
    const videoWrapper = this.refs.wrapper;
    const mediaQuery = '(max-width: 767px)';
    const useMobile = typeof window.matchMedia === 'function' && window.matchMedia(mediaQuery).matches;
    const requestedSource = useMobile ? 'mobile' : 'desktop';
    
    const template =
      this.querySelector(`template[data-video-source-template][data-video-source="${requestedSource}"]`) ||
      this.querySelector('template[data-video-source-template][data-video-source="desktop"]') ||
      this.querySelector('template[data-video-source-template]');

    if (!(template instanceof HTMLTemplateElement)) return;

    const selectedSource = template.dataset.videoSource || requestedSource;
    const selectedVideoType = template.dataset.videoType;
    const selectedVideoId = template.dataset.videoId;

    if (selectedVideoType) {
      this.dataset.videoType = selectedVideoType;
    }

    if (selectedVideoId) {
      this.dataset.videoId = selectedVideoId;
    }

    videoWrapper.innerHTML = '';
    videoWrapper.append(template.content.cloneNode(true));

    // 视频元素插入到了DOM，重新收集refs
    this.updatedCallback();

    this._videoCover = useMobile ? this.getDataset('mobileCover') || this.getDataset('cover') : this.getDataset('cover');
  }

  #initPlayer() {
    let controls = [];
    const settingControls = this.dataset.controls;
    if (settingControls == 'true') {
      controls = ['play-large', 'progress', 'current-time', 'duration', 'mute', 'volume', 'fullscreen'];
    } else if (settingControls == 'false') {
      controls = [];
    } else if (typeof settingControls === 'string' && settingControls.length > 0) {
      if (settingControls.includes('play')) {
        controls.push('play-large');
      }
      if (settingControls.includes('progress')) {
        controls.push('progress');
      }
      if (settingControls.includes('time')) {
        controls.push('current-time');
        controls.push('duration');
      }
      if (settingControls.includes('mute')) {
        controls.push('mute');
      }
      if (settingControls.includes('volume')) {
        controls.push('volume');
      }
      if (settingControls.includes('fullscreen')) {
        controls.push('fullscreen');
      }
    }

    const clickToPlay = controls.includes('play-large');

    this.player = new window.Plyr(this.refs.video, {
      controls: controls,
      autoplay: false,
      loop: {
        active: this.getDataset('loop', false)
      },
      muted: this.getDataset('muted'),
      clickToPlay: clickToPlay
    });
    if (this._videoCover) {
      this.player.poster = this._videoCover;
    }

    'progress,playing,play,pause,timeupdate,volumechange,seeking,seeked,ratechange,ended,enterfullscreen,exitfullscreen,ready'
      .split(',')
      .forEach((eventName) => {
        this.player.on(eventName, (event) => {
          this.emit(eventName);
          if (eventName === 'ready') {
            this.#handleReady();
          }
        });
      });
  }

  #handleReady() {
    this.#ready = true;
    // 静音放在options里设置有时候会失效，放到ready里设置一遍较稳定
    this.player.muted = this.getDataset('muted');
    this.setupAutoplay();
  }

  setupAutoplay() {
    if (this._cleanAutoplay) {
      this._cleanAutoplay();
    }
    if (this.getDataset('autoplay')) {
      this._cleanAutoplay = window.jxsInView({
        el: this,
        once: !this.getDataset('pauseOnLeave', false),
        enterCallback: () => {
          if (this.#ready) {
            this.player.muted = true;
            this.player.play();
          }
        },
        leaveCallback: () => {
          this.player.pause();
        }
      });
    }
  }

  emit(name) {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      details: {
        video: this
      } 
    }));
  }

  togglePlay() {
    this.player?.togglePlay();
  }

  play() {
    this.player?.play();
  }

  pause() {
    this.player?.pause();
  }

  restart() {
    if (this.player) {
      this.player.currentTime = 0;
      this.player.play();
    }
  }

  stop() {
    this.player?.stop();
  }

  /**
   * 
   * @param {boolean} enter true表示进入全屏，false表示退出全屏
   */
  fullscree(enter) {
    if (enter) {
      this.player?.fullscree.enter();
    } else {
      this.player?.fullscree.exit();
    }
  }

  get videoType() {
    return this.dataset.videoType;
  }

  get ready() {
    return this.#ready;
  }

  get loop() {
    return this.getDataset('loop');
  }

  set loop(value) {
    this.dataset.loop = value;
    this.player.loop = value;
  }

  get muted() {
    return this.getDataset('muted');
  }

  set muted(value) {
    this.dataset.muted = value;
    this.player.muted = value;
  }

  get autoplay() {
    return this.getDataset('autoplay');
  }

  set autoplay(value) {
    this.dataset.autoplay = value;
    this.setupAutoplay();
  }

  get pauseOnLeave() {
    return this.getDataset('pauseOnLeave');
  }

  set pauseOnLeave(value) {
    this.dataset.pauseOnLeave = value;
    this.setupAutoplay();
  }
}
customElements.define('jxs-video', JxsVideo);